/* ═══════════════════════════════════════════════════════════════════════════
   Tačke na mapi — pumpe, klijenti, carine, parkinzi, magacini.

   Mesta koja se stalno ponavljaju u radu, a nisu ni vozilo ni nalog. Unose se
   ovde, prikazuju se na GPS mapi kao pinovi, po tipu, sa mogućnošću da se ceo
   sloj ugasi.

   ── DVA IZVORA, NAMERNO ODVOJENA ──────────────────────────────────────────
   Sedište firme NIJE tačka iz ove tabele — čita se iz podataka o firmi
   (`company.Firma`), pa prati promenu adrese same firme umesto da se podatak
   drži na dva mesta. Ovde se samo postavlja njegova koordinata.

   ── KO ŠTA VIDI ───────────────────────────────────────────────────────────
   Tačka pripada firmi koja ju je unela. „Vidljivo mreži" kaže da li je vide i
   firme iz iste mreže — pumpa jeste zajednička stvar, a tačka kod klijenta ume
   da bude poslovna tajna. Tuđa tačka se vidi, ali se ne menja.
   ═══════════════════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import '../Administracija/settings.css';
import './TackeMapeHome.css';
import { useTranslation } from 'react-i18next';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useSelector } from 'react-redux';
import { AuthContext } from '../../Header/auth - context';
import { uzmiGresku } from '../../custom/greske';
import LocationPickerModal from '../Chat/LocationPickerModal';
import { izgledTipa } from './tackeMape';

const PRAZNO = {
    id: null, tip: null, Naziv: '', Adresa: '', Mesto: '',
    lat: null, lon: null, Napomena: '', VidljivoMrezi: true,
};

const TackeMapeHome = () => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const company = useSelector(state => state.company);
    const toast = useRef(null);

    const [tipovi, setTipovi] = useState([]);
    const [tacke, setTacke] = useState([]);
    const [sedista, setSedista] = useState([]);
    const [forma, setForma] = useState(PRAZNO);
    const [izabranId, setIzabranId] = useState(null);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [radi, setRadi] = useState(false);

    /* Jedan birač za dve stvari — tačku i sedište. `cilj` kaže šta se postavlja. */
    const [birac, setBirac] = useState(null);

    /* Čije se sedište podešava. Mreža firmi je posao istog čoveka, pa se ne
       traži prijava kao svaka firma ponaosob samo da bi joj se postavila tačka. */
    const [sedisteFirma, setSedisteFirma] = useState(null);
    /* Pogoci geokodiranja adrese iz podataka o firmi; null = prozor zatvoren. */
    const [predlozi, setPredlozi] = useState(null);
    const [trazim, setTrazim] = useState(false);

    const zaglavlja = useCallback(() => ({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + auth.token,
    }), [auth.token]);

    const javi = (severity, detail) => toast.current?.show({
        severity,
        summary: severity === 'error' ? t('common.greska') : t('common.uspeh'),
        detail, life: 4000,
    });

    const naGresku = useCallback((e) => uzmiGresku(e, t('common.greska'))
        .then(m => toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: m.message, life: 5000 })),
        [t]);

    const zovi = useCallback((putanja, telo = {}) =>
        fetch(process.env.REACT_APP_BACKEND_URL + '/tacke' + putanja, {
            method: 'POST', headers: zaglavlja(), body: JSON.stringify(telo),
        }).then(r => (r.ok ? r.json() : Promise.reject(r))), [zaglavlja]);

    const ucitaj = useCallback(() => {
        zovi('/')
            .then(d => {
                if (d?.message) { javi('warn', d.message); return; }
                setTipovi(d.TipoviDB || []);
                setTacke(d.TackeDB || []);
                setSedista(d.SedistaDB || []);
            })
            .catch(naGresku);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zovi, naGresku]);

    useEffect(() => { ucitaj(); }, [ucitaj]);

    /* Podrazumevano se podešava sopstvena firma — nju operater i traži u 9 od
       10 slučajeva; ostale su tu kad neko vodi celu mrežu. */
    useEffect(() => {
        if (sedisteFirma != null || !sedista.length) return;
        const moja = sedista.find(x => x.moja === 1) || sedista[0];
        setSedisteFirma(moja.firma_id);
    }, [sedista, sedisteFirma]);

    const izabranoSediste = sedista.find(x => x.firma_id === sedisteFirma) || null;
    const imaKoordinatu = izabranoSediste && izabranoSediste.lat != null && izabranoSediste.lon != null;
    const firmeOpcije = sedista.map(x => ({ value: x.firma_id, label: x.Naziv }));

    /* Klik na isti red gasi izbor i prazni formu — isti obrazac kao na ostalim
       šifarnicima, pa „Novi" dugme nije ni potrebno. */
    const izaberi = (red) => {
        setIzabranId(prev => {
            const isti = String(prev) === String(red.id);
            if (isti) { setForma(PRAZNO); return null; }
            setForma({
                id: red.id, tip: red.tip, Naziv: red.Naziv,
                Adresa: red.Adresa || '', Mesto: red.Mesto || '',
                lat: red.lat, lon: red.lon, Napomena: red.Napomena || '',
                VidljivoMrezi: !!red.VidljivoMrezi,
            });
            return red.id;
        });
    };

    const sacuvaj = () => {
        if (!forma.Naziv.trim() || !forma.tip || forma.lat == null || forma.lon == null) {
            javi('warn', t('tacke.obavezna'));
            return;
        }
        setRadi(true);
        zovi('/sacuvaj', forma)
            .then(d => {
                javi('success', d?.message || t('common.sacuvano'));
                setForma(PRAZNO); setIzabranId(null);
                ucitaj();
            })
            .catch(naGresku)
            .finally(() => setRadi(false));
    };

    const obrisi = (red) => confirmDialog({
        message: t('tacke.potvrdaBrisanja'),
        acceptLabel: t('common.da'), rejectLabel: t('common.ne'),
        icon: 'pi pi-exclamation-triangle', acceptClassName: 'p-button-danger',
        accept: () => zovi('/obrisi', { id: red.id })
            .then(d => {
                javi('success', d?.message || t('common.sacuvano'));
                if (String(izabranId) === String(red.id)) { setForma(PRAZNO); setIzabranId(null); }
                ucitaj();
            })
            .catch(naGresku),
    });

    const postaviSediste = (lat, lon) => zovi('/sediste', { lat, lon, firma_id: sedisteFirma })
        .then(d => { javi('success', d?.message || t('common.sacuvano')); ucitaj(); })
        .catch(naGresku);

    /* Adresa se NE šalje sa ekrana — server je čita iz podataka o firmi i
       geokodira. Pogodaka ume da bude više (ista ulica u više mesta), pa
       poslednju reč ima operater. */
    const predloziSaAdrese = () => {
        if (sedisteFirma == null) return;
        setTrazim(true);
        zovi('/sediste/predlog', { firma_id: sedisteFirma })
            .then(d => {
                if (!d?.predlozi?.length) {
                    javi('warn', d?.message || t('tacke.nemaPredloga'));
                    return;
                }
                setPredlozi(d.predlozi);
            })
            .catch(naGresku)
            .finally(() => setTrazim(false));
    };

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('TackeNaMapi');
        const headers = [t('tacke.tip'), t('tacke.naziv'), t('admin.adresa'), t('admin.mesto'),
            t('tacke.koordinate'), t('nova.napomena'), t('tacke.vidljivoMrezi')];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
            cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
        });
        (tacke || []).forEach(r => {
            const row = worksheet.addRow([
                r.tipOpis, r.Naziv, r.Adresa, r.Mesto,
                `${r.lat}, ${r.lon}`, r.Napomena,
                r.VidljivoMrezi ? t('common.da') : t('common.ne'),
            ]);
            row.eachCell(cell => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });
        worksheet.columns.forEach(col => {
            let max = 10;
            col.eachCell(cell => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade' }).replace(/,/g, '');
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
            `${t('tacke.naslov')} - ${company?.companyName || ''} - ${dt}.xlsx`);
    };

    const tableHeader = (
        <div className="flex align-items-center gap-2 export-buttons" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />
            </div>
            <input type="search" placeholder={t('nova.phPretraga')} className="p-inputtext p-component"
                   onInput={e => setGlobalFilter(e.target.value)} />
        </div>
    );

    const koloneTip = (red) => {
        const izgled = izgledTipa(red.tipOpis);
        return (
            <span className="tm-tip">
                <span className="tm-tip-boja" style={{ background: izgled.boja }}>
                    <i className={`pi ${izgled.ikona}`} />
                </span>
                {red.tipOpis}
            </span>
        );
    };

    const koloneRadnje = (red) => (
        red.moja === 1 ? (
            <div className="tm-radnje">
                <Button type="button" icon="pi pi-pencil" className="p-button-text p-button-sm"
                        onClick={() => izaberi(red)} />
                <Button type="button" icon="pi pi-trash" severity="danger" className="p-button-text p-button-sm"
                        onClick={() => obrisi(red)} />
            </div>
        ) : (
            <span className="tm-tudja" title={t('tacke.tudjaTacka')}>
                <i className="pi pi-lock" /> {red.firma}
            </span>
        )
    );

    return (
        <div className="col-12 tm-koren">
            <Toast ref={toast} />
            <Tooltip target=".export-buttons>button" position="bottom" />

            {birac && (
                <LocationPickerModal
                    onClose={() => setBirac(null)}
                    onConfirm={(lat, lon) => {
                        const tacka = { lat: Number(lat.toFixed(6)), lon: Number(lon.toFixed(6)) };
                        if (birac === 'sediste') postaviSediste(tacka.lat, tacka.lon);
                        else setForma(prev => ({ ...prev, ...tacka }));
                        setBirac(null);
                    }}
                />
            )}

            {/* Sedište firme stoji izdvojeno: nije tačka iz tabele nego podatak o
                samoj firmi, pa i ekran to razdvaja. Firma se bira — spisak je
                cela mreža (config.CompanyInCompany), i one bez postavljene tačke. */}
            <Card className="tm-sediste">
                <div className="tm-sediste-red">
                    <div className="tm-sediste-podaci">
                        <span className="tm-mikro">{t('tacke.sediste')}</span>
                        {firmeOpcije.length > 1
                            ? <Dropdown value={sedisteFirma} options={firmeOpcije}
                                        onChange={e => setSedisteFirma(e.value)}
                                        className="tm-sediste-firma" />
                            : <strong>{izabranoSediste?.Naziv || company?.companyName || '—'}</strong>}
                        <div className="tm-tiho">
                            {izabranoSediste?.PunaAdresa || t('tacke.nemaAdrese')}
                        </div>
                        {imaKoordinatu
                            ? <div className="tm-tiho">{izabranoSediste.lat}, {izabranoSediste.lon}</div>
                            : <div className="tm-upozorenje">
                                <i className="pi pi-exclamation-circle" /> {t('tacke.sedisteNijePostavljeno')}
                              </div>}
                    </div>
                    <div className="tm-sediste-radnje">
                        {/* Predlog sa adrese je prečica, ne zamena: adresa u podacima o
                            firmi ume da bude nepotpuna, pa mapa ostaje tu. */}
                        <Button type="button" icon="pi pi-search-plus" severity="secondary" outlined
                                label={t('tacke.predlogAdrese')} loading={trazim}
                                disabled={sedisteFirma == null}
                                onClick={predloziSaAdrese} />
                        <Button type="button" icon="pi pi-map" severity="secondary" outlined
                                label={t('tacke.postaviSediste')}
                                disabled={sedisteFirma == null}
                                onClick={() => setBirac('sediste')} />
                    </div>
                </div>
            </Card>

            {/* Pogoci sa adrese firme. Prikazuju se i kad je pogodak jedan —
                koordinata se upisuje u podatke o firmi, pa je potvrda na mestu. */}
            <Dialog visible={!!predlozi} onHide={() => setPredlozi(null)}
                    header={t('tacke.predloziNaslov')} style={{ width: '32rem' }}
                    breakpoints={{ '768px': '92vw' }} modal draggable={false} resizable={false}>
                <div className="tm-predlozi">
                    {(predlozi || []).map((x, i) => (
                        <button key={i} type="button" className="tm-predlog"
                                onClick={() => { setPredlozi(null); postaviSediste(x.lat, x.lon); }}>
                            <i className="pi pi-map-marker" />
                            <span>
                                <strong>{x.naziv}</strong>
                                <em>{x.lat}, {x.lon}</em>
                            </span>
                        </button>
                    ))}
                </div>
            </Dialog>

            <Card>
                <div className="tm-forma">
                    <div className="tm-polje">
                        <label htmlFor="tip">{t('tacke.tip')}:</label>
                        <Dropdown id="tip" value={forma.tip} options={tipovi}
                                  onChange={e => setForma(p => ({ ...p, tip: e.value }))}
                                  placeholder={t('common.choose')} />
                    </div>
                    <div className="tm-polje tm-siroko">
                        <label htmlFor="Naziv">{t('tacke.naziv')}:</label>
                        <InputText id="Naziv" value={forma.Naziv}
                                   onChange={e => setForma(p => ({ ...p, Naziv: e.target.value }))} />
                    </div>
                    <div className="tm-polje">
                        <label htmlFor="Adresa">{t('admin.adresa')}:</label>
                        <InputText id="Adresa" value={forma.Adresa}
                                   onChange={e => setForma(p => ({ ...p, Adresa: e.target.value }))} />
                    </div>
                    <div className="tm-polje">
                        <label htmlFor="Mesto">{t('admin.mesto')}:</label>
                        <InputText id="Mesto" value={forma.Mesto}
                                   onChange={e => setForma(p => ({ ...p, Mesto: e.target.value }))} />
                    </div>

                    <div className="tm-polje">
                        <label>{t('tacke.koordinate')}:</label>
                        {/* Koordinata se bira na mapi, ne kuca: adresa ume da promaši
                            krug firme, a pin ne. */}
                        {forma.lat != null && forma.lon != null ? (
                            <span className="tm-koord">
                                <i className="pi pi-map-marker" />
                                {forma.lat}, {forma.lon}
                                <Button type="button" icon="pi pi-pencil" className="p-button-text p-button-sm"
                                        onClick={() => setBirac('tacka')} />
                            </span>
                        ) : (
                            <Button type="button" icon="pi pi-map" severity="secondary" outlined
                                    label={t('tacke.postaviNaMapi')} onClick={() => setBirac('tacka')} />
                        )}
                    </div>

                    <div className="tm-polje tm-siroko">
                        <label htmlFor="Napomena">{t('nova.napomena')}:</label>
                        <InputTextarea id="Napomena" rows={1} autoResize value={forma.Napomena}
                                       onChange={e => setForma(p => ({ ...p, Napomena: e.target.value }))} />
                    </div>

                    <div className="tm-polje tm-kvacica">
                        <Checkbox inputId="VidljivoMrezi" checked={forma.VidljivoMrezi}
                                  onChange={e => setForma(p => ({ ...p, VidljivoMrezi: e.checked }))} />
                        <label htmlFor="VidljivoMrezi">{t('tacke.vidljivoMrezi')}</label>
                    </div>
                </div>

                <button type="button" onClick={sacuvaj} disabled={radi}
                        style={{ minWidth: '20%', marginTop: '2%' }} className="btn btn-success">
                    {izabranId ? t('common.save') : t('common.unos')}
                </button>
            </Card>

            <Card>
                <DataTable value={tacke || []} paginator rows={10}
                           globalFilter={globalFilter} scrollable scrollHeight="50vh"
                           header={tableHeader} resizableColumns columnResizeMode="expand"
                           className="turePregledTable"
                           emptyMessage={t('tacke.nemaTacaka')}
                           rowClassName={r => String(r.id) === String(izabranId) ? { 'selected-row': true } : {}}>
                    <Column header={t('tacke.tip')} body={koloneTip} style={{ minWidth: '150px' }} />
                    <Column field="Naziv" header={t('tacke.naziv')} style={{ minWidth: '180px' }} />
                    <Column header={t('admin.adresa')} style={{ minWidth: '200px' }}
                            body={r => [r.Adresa, r.Mesto].filter(Boolean).join(', ')} />
                    <Column header={t('tacke.koordinate')} style={{ minWidth: '160px' }}
                            body={r => (
                                <a href={`https://www.google.com/maps?q=${r.lat},${r.lon}`}
                                   target="_blank" rel="noreferrer">{r.lat}, {r.lon}</a>
                            )} />
                    <Column field="Napomena" header={t('nova.napomena')} style={{ minWidth: '160px' }} />
                    <Column header={t('tacke.vidljivoMrezi')} style={{ minWidth: '110px' }}
                            body={r => <Tag severity={r.VidljivoMrezi ? 'success' : 'secondary'}
                                            value={r.VidljivoMrezi ? t('common.da') : t('common.ne')} />} />
                    <Column header="" body={koloneRadnje} style={{ minWidth: '120px' }} />
                </DataTable>
            </Card>
        </div>
    );
};

export default TackeMapeHome;
